import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import isEqual from 'lodash-es/isEqual';
import type { ListRange, VirtuosoHandle } from 'react-virtuoso';
import { PaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { TextField, useControlledField } from '@frontend/design-system';
import { useUserSearch } from '../../../hooks/use-user-search';
import { useKeywordSearchStore } from '../../../providers/keyword-search.provider/keyword-search.provider';
import { useTeamChatSelector } from '../../../providers/team-chat.provider';
import { getUserFullName } from '../../../utils';
import { FilterChipPopover } from '../../common/filter-chip-popover/filter-chip-popover.component';
import { SenderListItemComponent } from './sender-list-item.component';

const LIST_HEIGHT = 248;
const NO_ACTIVE_ITEM = -1;

interface SenderFilterProps {
  focusSearchBar: () => void;
}

export const SenderFilter: FC<SenderFilterProps> = ({ focusSearchBar }) => {
  const { t } = useTranslation('team-chat');
  const { sender, setSender } = useKeywordSearchStore(['sender', 'setSender']);
  const currentUser = useTeamChatSelector((ctx) => ctx.user);
  const { onChangeSearchTerm, searchTerm, usersToDisplay, isLoading, allUsers } = useUserSearch(false, true);

  const [selectedMembers, setSelectedMembers] = useState<string[]>(sender);

  // Keyboard navigation state
  const [activeIndex, setActiveIndex] = useState(NO_ACTIVE_ITEM);
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const visibleRangeRef = useRef<ListRange>({ startIndex: 0, endIndex: 0 });

  // Reset keyboard focus when the list data changes (e.g. after a search)
  useEffect(() => {
    setActiveIndex(NO_ACTIVE_ITEM);
  }, [usersToDisplay]);

  // Scroll the list only when the active item falls outside the visible range.
  // This mirrors the DropdownFilterChipField's useListNavigation behavior
  // where items in view just highlight without scrolling.
  useEffect(() => {
    if (activeIndex < 0) return;

    const { startIndex, endIndex } = visibleRangeRef.current;
    if (activeIndex <= startIndex) {
      virtuosoRef.current?.scrollToIndex({ index: activeIndex, align: 'start' });
    } else if (activeIndex >= endIndex) {
      virtuosoRef.current?.scrollToIndex({ index: activeIndex, align: 'end' });
    }
  }, [activeIndex]);

  const searchFieldProps = useControlledField({
    type: 'text',
    placeholder: t('Search members'),
    value: searchTerm,
    onChange: onChangeSearchTerm,
  });

  const chipLabel = useMemo(() => {
    if (sender.length === 0) return t('From: @member');

    if (sender.length === 1) {
      const match = allUsers?.find((user) => user.userID === sender[0]);
      if (match) return t('From: @{{label}}', { label: getUserFullName(match.firstName, match.lastName) });

      return t('From: 1 member');
    }

    return t('From: {{count}} members', { count: sender.length });
  }, [sender, allUsers, t]);

  const sortedUsersToDisplay = useMemo(() => {
    const { selected, nonSelected } = usersToDisplay.reduce<{
      selected: typeof usersToDisplay;
      nonSelected: typeof usersToDisplay;
    }>(
      (acc, user) => {
        const isSelected = sender.includes(user.userID);
        if (isSelected) {
          acc.selected.push(user);
        } else {
          acc.nonSelected.push(user);
        }
        return acc;
      },
      { selected: [], nonSelected: [] }
    );
    return [...selected, ...nonSelected];
  }, [sender, usersToDisplay]);

  const onClear = useCallback(() => {
    setSelectedMembers([]);
    setSender([]);
  }, [setSender]);

  const onApply = useCallback(() => {
    setSender(selectedMembers);
    focusSearchBar();
  }, [setSender, selectedMembers, focusSearchBar]);

  const toggleMember = useCallback((id: string) => {
    setSelectedMembers((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]));
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Will rely on default browser focus styles for buttons, so ignore keyboard events
      // originating from button elements (e.g. the clear button)
      if (e.target instanceof HTMLButtonElement) return;
      if (!usersToDisplay?.length) return;

      const lastIndex = usersToDisplay.length - 1;
      const isValidActiveItem = activeIndex >= 0 && activeIndex <= lastIndex;

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          setActiveIndex((prev) => (prev < lastIndex ? prev + 1 : 0));
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          setActiveIndex((prev) => (prev > 0 ? prev - 1 : lastIndex));
          break;
        }
        case 'Enter': {
          if (isValidActiveItem) {
            e.preventDefault();
            toggleMember(usersToDisplay[activeIndex].userID);
          }
          break;
        }
        case ' ': {
          // Allow space in text inputs for search
          if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
          if (isValidActiveItem) {
            e.preventDefault();
            toggleMember(usersToDisplay[activeIndex].userID);
          }
          break;
        }
      }
    },
    [usersToDisplay, activeIndex, toggleMember]
  );

  const handleRangeChanged = useCallback((range: ListRange) => {
    visibleRangeRef.current = range;
  }, []);

  return (
    <FilterChipPopover
      value={chipLabel}
      onClear={onClear}
      onApply={onApply}
      isClearDisabled={selectedMembers.length === 0}
      isApplyDisabled={isEqual(selectedMembers, sender)}
      onKeyDown={handleKeyDown}
      clearButtonTrackingId='team-chat-2.0-keyword-search-sender-filter-clear-button'
      applyButtonTrackingId='team-chat-2.0-keyword-search-sender-filter-apply-button'
      trackingId='team-chat-2.0-keyword-search-sender-filter-button'
      Header={
        <TextField
          name='member-filter-user-search'
          label=''
          startAdornment={<Icon name='search' />}
          clearable
          containerCss={styles.textField}
          {...searchFieldProps}
        />
      }
    >
      <PaginatedList
        listData={sortedUsersToDisplay}
        isLoading={isLoading}
        endOfListText={''}
        height={LIST_HEIGHT}
        virtuosoRef={virtuosoRef}
        rangeChanged={handleRangeChanged}
        renderListItem={({ listItem: user, index }) => (
          <SenderListItemComponent
            key={user.userID}
            user={user}
            isSelected={selectedMembers.includes(user.userID)}
            isActive={index === activeIndex}
            onChange={toggleMember}
            isSelfUser={currentUser?.userID === user.userID}
          />
        )}
        emptyStateConfig={{
          disableGraphic: true,
        }}
      />
    </FilterChipPopover>
  );
};

const styles = {
  textField: css({
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.colors.neutral20}`,
  }),
};
